import { useEffect, useState } from "react";
import CSDTable, {
  TableProps,
} from "@cloudscape-design/components-themed/table";
import {
  Box,
  SpaceBetween,
  Header,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import {
  COMMON_LABELS,
  getDivisionLabel,
  merchantTypeDivisionList,
} from "../default-constants";
import { LABELS } from "./constants";
import { Link } from "react-router-dom";
import {
  adjustPageIndex,
  getHeaderCounterText,
  getIHRLabel,
  getPagedItems,
} from "../../utils/common";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import Preference, { ContentOption } from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { useCodelistHook } from "../../app/useCodelistHook";
import { useGetIndividualHandlingConfirmationQuery } from "../../features/api";
import {
  setColumnWidths,
  setPageIndex,
  changePageSize,
} from "../../features/slices/individual-handling-list";
import { IndividualHandlingSearchResponse } from "../../features/api-interfaces";
import { formatLabelsFromCodes } from "../../utils/format";

interface ITable {
  isBackFromDetail: boolean;
  skip: boolean;
}

/**
 * 個別取扱確認結果一覧 一覧エリア
 */
const Table = (props: ITable) => {
  const { isBackFromDetail, skip } = props;
  const dispatch = useAppDispatch();
  const { applicationType, individualHandlingReason, applicationFormType } =
    useCodelistHook();
  const { searchCriteria, tableConfig } = useAppSelector(
    (state) => state.individualHandlingList
  );
  const { pageSize, pageIndex, columnWidths } = tableConfig;
  const { data, isFetching, refetch } =
    useGetIndividualHandlingConfirmationQuery(searchCriteria, { skip });
  const columnItemBase: TableProps.ColumnDefinition<IndividualHandlingSearchResponse>[] =
    [
      {
        id: "individualHandlingReason",
        header: LABELS.INDIVIDUAL_HANDLING_REASON,
        cell: (item) =>
          getIHRLabel(
            individualHandlingReason,
            item.individualHandlingReason,
            searchCriteria.individualHandlingReason
          ),
      },
      {
        id: "status",
        header: LABELS.STATUS,
        cell: (item) => item.status,
      },
      {
        id: "applicationNo",
        header: LABELS.APPLICATION_NO,
        cell: (item) => (
          <Link
            to={`/individual-handling-confirmation-result/${item.applicationNo}?isBackToList=true`}
          >
            {item.applicationNo}
          </Link>
        ),
      },

      {
        id: "applicationDate",
        header: LABELS.APPLICATION_DATE,
        cell: (item) => item.applicationDate,
      },
      {
        id: "applicationTypeCode",
        header: LABELS.APPLICATION_TYPE_CODE,
        cell: (item) =>
          getDivisionLabel(applicationType, item.applicationTypeCode),
      },
      {
        id: "contractType",
        header: LABELS.CONTRACT_TYPE,
        cell: (item) => item.contractType,
      },
      {
        id: "contractRoute",
        header: LABELS.CONTRACT_ROUTE,
        cell: (item) =>
          item.contractType === LABELS.CONTRACT_COMPREHENSIVE
            ? item.contractRoute
            : COMMON_LABELS.HYPHEN,
      },
      {
        id: "particularRateFlag",
        header: LABELS.PARTICULAR_RATE_FLAG,
        cell: (item) => item.particularRateFlag ?? COMMON_LABELS.HYPHEN,
      },
      {
        id: "companyName",
        header: LABELS.COMPANY_NAME,
        cell: (item) => item.companyName,
      },
      {
        id: "merchantName",
        header: LABELS.MERCHANTS_NAME,
        cell: (item) => item.merchantName,
      },
      {
        id: "merchantTypeNo",
        header: LABELS.MERCHANT_TYPE,
        cell: (item) =>
          getDivisionLabel(
            merchantTypeDivisionList,
            String(item.merchantTypeNo)
          ),
      },
      {
        id: "industry",
        header: LABELS.INDUSTRY,
        cell: (item) => item.industry,
      },
      {
        id: "applicationFormType",
        header: LABELS.APPLICATION_FORM_TYPE,
        cell: (item) =>
          formatLabelsFromCodes(
            applicationFormType,
            item.applicationFormTypeCodes
          ),
      },
    ];
  const columnDefinitions = columnItemBase.map((column, index) => {
    const width = isBackFromDetail ? columnWidths[index] : undefined;
    return { ...column, width };
  });
  const options = columnItemBase.map((column) => ({
    id: column.id,
    label: column.header,
  })) as ContentOption[];
  const [pagedItems, setPagedItems] = useState<
    IndividualHandlingSearchResponse[]
  >([]);
  const [allItems, setAllItems] = useState<Object[]>([]);

  useEffect(() => {
    if (data !== undefined && !isFetching) {
      setPagedItems(
        getPagedItems(
          data,
          pageIndex,
          pageSize
        ) as IndividualHandlingSearchResponse[]
      );
      setAllItems(data);
      setCurrentPageIndex(adjustPageIndex(data.length, pageSize, pageIndex));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageIndex, pageSize]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * テーブルのカラム幅を変更したときの処理.
   * @param {number[]} widths カラム幅リスト
   */
  const onColumnWidthsChange = (widths: number[]) =>
    dispatch(setColumnWidths(widths));

  /**
   * ページ番号を変更したときの処理.
   * @param {number} currentPageIndex 現在のページ番号
   */
  const setCurrentPageIndex = (currentPageIndex: number) =>
    dispatch(setPageIndex(currentPageIndex));

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) =>
    dispatch(changePageSize(detail.pageSize!));

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      trackBy="ruleNo"
      items={pagedItems}
      onColumnWidthsChange={({ detail: { widths } }) =>
        onColumnWidthsChange(widths as number[])
      }
      resizableColumns
      sortingDescending
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <Header>
            {LABELS.INDIVIDUAL_HANDLING_CONFIRMATION_RESULT}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={pageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={{ pageSize, visibleContent: [] }}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
